import React from 'react';
import MsDyn365 from '@msdyn365-commerce/core';
import {
    useFloating,
    offset as fuiOffset,
    flip,
    shift,
    size,
    autoUpdate,
    useDismiss,
    useRole,
    useInteractions,
    FloatingPortal,
    FloatingFocusManager,
    Placement
} from '@floating-ui/react';

type DropdownOpenListener = (source: symbol) => void;
const dropdownOpenListeners = new Set<DropdownOpenListener>();

// Using Floating UI to portal to document.body, avoid overflow clipping, and
// auto-position with flip/shift. Inline comments mark key bits.

type LocalDropdownProps = {
    trigger: (p: { ref: React.Ref<HTMLElement>; onClick: (event?: React.MouseEvent<HTMLElement>) => void; 'aria-expanded': boolean }) => React.ReactNode;
    children: (close: () => void) => React.ReactNode;
    matchWidth?: boolean;
    offset?: number;
    placement?: Placement;
    floatClassName?: string;
    backdropClassName?: string;
    openBodyClassName?: string;
    popupRole?: 'dialog' | 'menu';
    floatingId?: string;
    labelledBy?: string;
    initialFocusSelector?: string;
    returnFocusOnClose?: boolean;
    alignControlToReference?: boolean;
    initialHoverSelector?: string;
    openAnimationMs?: number;
    closeAnimationMs?: number;
    closeAnimationSelector?: string;
    bottomSheetBreakpoint?: number;
    bottomSheetDismissible?: boolean;
    preserveAlignmentOnFlip?: boolean;
};

export function LocalDropdown({
    trigger,
    children,
    matchWidth = true,
    offset = 4,
    placement = 'bottom-end',
    floatClassName,
    backdropClassName,
    openBodyClassName,
    popupRole = 'menu',
    floatingId,
    labelledBy,
    initialFocusSelector,
    returnFocusOnClose = false,
    alignControlToReference = false,
    initialHoverSelector,
    openAnimationMs = 110,
    closeAnimationMs = 0,
    closeAnimationSelector,
    bottomSheetBreakpoint,
    bottomSheetDismissible = false,
    preserveAlignmentOnFlip = false
}: LocalDropdownProps) {
    const [open, setOpen] = React.useState(false);
    const [isClosing, setIsClosing] = React.useState(false);
    const wasOpenRef = React.useRef(false);
    const closeTimerRef = React.useRef<number>();
    const lastFloatingElementRef = React.useRef<HTMLDivElement | null>(null);
    const shouldReturnFocusOnCloseRef = React.useRef(false);
    const dropdownIdRef = React.useRef(Symbol('local-dropdown'));
    const sheetDragRef = React.useRef<{ pointerId: number; startY: number; offsetY: number; target: HTMLElement }>();
    const sheetDragTimeoutRef = React.useRef<number>();

    React.useEffect(() => () => {
        if (closeTimerRef.current) {
            window.clearTimeout(closeTimerRef.current);
        }
        if (sheetDragTimeoutRef.current) {
            window.clearTimeout(sheetDragTimeoutRef.current);
        }
    }, []);

    const handleOpenChange = React.useCallback((nextOpen: boolean) => {
        if (closeTimerRef.current) {
            window.clearTimeout(closeTimerRef.current);
        }
        if (sheetDragTimeoutRef.current) {
            window.clearTimeout(sheetDragTimeoutRef.current);
        }

        if (nextOpen || !closeAnimationMs) {
            setIsClosing(false);
            shouldReturnFocusOnCloseRef.current = false;
            setOpen(nextOpen);

            if (nextOpen) {
                dropdownOpenListeners.forEach(listener => listener(dropdownIdRef.current));
            }
            return;
        }

        const sheetTarget = closeAnimationSelector
            ? lastFloatingElementRef.current?.querySelector<HTMLElement>(closeAnimationSelector)
            : lastFloatingElementRef.current;
        sheetTarget?.style.removeProperty('animation');
        sheetTarget?.style.removeProperty('transition');
        sheetTarget?.style.removeProperty('transform');
        setIsClosing(true);
        // Keep the existing floating element mounted for its exit animation,
        // but disable its interactions while another popup can be opened.
        closeTimerRef.current = window.setTimeout(() => {
            shouldReturnFocusOnCloseRef.current = returnFocusOnClose
                && !!lastFloatingElementRef.current?.contains(document.activeElement);
            setIsClosing(false);
            setOpen(false);
        }, closeAnimationMs);
    }, [closeAnimationMs, returnFocusOnClose]);

    React.useEffect(() => {
        const closeWhenAnotherDropdownOpens: DropdownOpenListener = source => {
            if (source !== dropdownIdRef.current && (open || isClosing)) {
                handleOpenChange(false);
            }
        };

        dropdownOpenListeners.add(closeWhenAnotherDropdownOpens);
        return () => {
            dropdownOpenListeners.delete(closeWhenAnotherDropdownOpens);
        };
    }, [handleOpenChange, isClosing, open]);

    React.useEffect(() => {
        if (open && openBodyClassName) {
            document.body.classList.add(openBodyClassName);
        } else if (openBodyClassName) {
            document.body.classList.remove(openBodyClassName);
        }
    }, [open, openBodyClassName]);

    // Floating UI core: compute styles + keep updated on scroll/resize/layout
    const { refs, floatingStyles, context, placement: resolvedPlacement } = useFloating({
        open,
        onOpenChange: handleOpenChange,
        placement,
        middleware: [
            fuiOffset(offset),
            flip({ padding: 8, flipAlignment: !preserveAlignmentOnFlip }), // flip above if there's not enough room below
            shift({ padding: 8 }), // nudge inside viewport if overflowing horizontally
            size({
                // Ensure a minimum width equal to the reference (or 400px),
                // and cap height to available viewport space with scroll.
                apply({ rects, availableHeight, elements, ...params }) {
                    if (matchWidth) {
                        const w = Math.max(rects.reference.width, 400);
                        Object.assign(elements.floating.style, { minWidth: `${w}px` });
                    }
                    /* Object.assign(elements.floating.style, {
                        maxHeight: `${Math.max(200, Math.floor(availableHeight - 8))}px`
                    }); */
                }
            })
        ],
        whileElementsMounted: autoUpdate
    });

    // Use resolved pixel values for a closing shape animation. CSS keyframes can
    // interpolate the opening clip-path, but can snap on the reverse path when
    // its target comes from custom properties.
    React.useEffect(() => {
        if (!isClosing || !closeAnimationSelector
            || (bottomSheetBreakpoint && window.matchMedia(`(max-width: ${bottomSheetBreakpoint}px)`).matches)) {
            return;
        }

        const animationTarget = refs.floating.current?.querySelector<HTMLElement>(closeAnimationSelector);
        if (!animationTarget) {
            return;
        }

        const computedStyle = window.getComputedStyle(animationTarget);
        const triggerTop = computedStyle.getPropertyValue('--compact-trigger-top').trim() || '48px';
        const triggerRight = computedStyle.getPropertyValue('--compact-trigger-right').trim() || '0px';
        const triggerBottom = computedStyle.getPropertyValue('--compact-trigger-bottom').trim() || 'calc(100% - 84px)';
        const triggerLeft = computedStyle.getPropertyValue('--compact-trigger-left').trim() || 'calc(100% - 36px)';
        const triggerClipPath = `inset(${triggerTop} ${triggerRight} ${triggerBottom} ${triggerLeft} round 8px)`;
        const animation = animationTarget.animate(
            [
                { clipPath: 'inset(0 round 8px)' },
                { clipPath: triggerClipPath }
            ],
            {
                duration: closeAnimationMs,
                easing: 'cubic-bezier(.4, 0, 1, 1)',
                fill: 'forwards'
            }
        );

        return () => animation.cancel();
    }, [bottomSheetBreakpoint, closeAnimationMs, closeAnimationSelector, isClosing, refs.floating]);

    // Interactions: click to toggle, Esc/outside click to dismiss, and the
    // caller-selected popup role.
    const dismiss = useDismiss(context, {
        enabled: !isClosing,
        escapeKey: true,
        outsidePressEvent: 'pointerdown',
        outsidePress: e => {
            const t = e.target as Node;
            return !refs.floating.current?.contains(t) && !refs.domReference.current?.contains(t);
        }
    });
    const role = useRole(context, { role: popupRole });
    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

    React.useEffect(() => {
        if (returnFocusOnClose && shouldReturnFocusOnCloseRef.current && wasOpenRef.current && !open) {
            (refs.domReference.current as HTMLElement | null)?.focus({ preventScroll: true });
        }

        wasOpenRef.current = open;
    }, [open, refs.domReference, returnFocusOnClose]);

    const updatePanelAlignment = React.useCallback(() => {
        const element = refs.floating.current;
        const reference = refs.domReference.current as HTMLElement | null;
        if (!element || !reference || !alignControlToReference) {
            return;
        }

        const referenceRect = reference.getBoundingClientRect();
        const floatingRect = element.getBoundingClientRect();
        const animationTarget = closeAnimationSelector
            ? element.querySelector<HTMLElement>(closeAnimationSelector) || element
            : element;
        const preferredLeft = referenceRect.right - floatingRect.width;
        const wasShiftedRight = floatingRect.left > preferredLeft + 1;

        element.dataset.referenceSide = wasShiftedRight ? 'left' : 'right';
        // Setting referenceSide can offset the panel surface. Measure only
        // after applying it so the trigger rectangle is in that surface's
        // own coordinate system.
        const animationTargetRect = animationTarget.getBoundingClientRect();
        animationTarget.style.setProperty('--compact-trigger-top', `${referenceRect.top - animationTargetRect.top}px`);
        animationTarget.style.setProperty('--compact-trigger-right', `${animationTargetRect.right - referenceRect.right}px`);
        animationTarget.style.setProperty('--compact-trigger-bottom', `${animationTargetRect.bottom - referenceRect.bottom}px`);
        animationTarget.style.setProperty('--compact-trigger-left', `${referenceRect.left - animationTargetRect.left}px`);
    }, [alignControlToReference, closeAnimationSelector, refs.domReference, refs.floating]);

    React.useEffect(() => {
        if (!open || !alignControlToReference) {
            return;
        }

        const onResize = () => window.requestAnimationFrame(updatePanelAlignment);
        window.addEventListener('resize', onResize);
        onResize();
        return () => window.removeEventListener('resize', onResize);
    }, [alignControlToReference, open, updatePanelAlignment]);

    const setFloatingRef = React.useCallback((element: HTMLDivElement | null) => {
        refs.setFloating(element);

        if (element) {
            lastFloatingElementRef.current = element;
        }

        // The panel's children have mounted when this ref is assigned. Focus
        // synchronously here rather than in an effect, so a following key press
        // cannot be handled by the still-focused trigger or carousel.
        if (element && initialFocusSelector) {
            element.querySelector<HTMLElement>(initialFocusSelector)?.focus({ preventScroll: true });
        }

        if (element && alignControlToReference) {
            window.requestAnimationFrame(updatePanelAlignment);
        }

        const isBottomSheet = !!bottomSheetBreakpoint && window.matchMedia(`(max-width: ${bottomSheetBreakpoint}px)`).matches;
        if (element && initialHoverSelector && !isBottomSheet) {
            const hoverTarget = element.querySelector<HTMLElement>(initialHoverSelector);
            element.classList.add('is-initial-pointer-hover');
            hoverTarget?.classList.add('is-pointer-hovered');
            window.addEventListener('pointermove', () => {
                element.classList.remove('is-initial-pointer-hover');
                hoverTarget?.classList.remove('is-pointer-hovered');
            }, { once: true });
        }
    }, [alignControlToReference, bottomSheetBreakpoint, initialFocusSelector, initialHoverSelector, refs, updatePanelAlignment]);

    const handleBottomSheetPointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (!bottomSheetDismissible || !bottomSheetBreakpoint
            || !window.matchMedia(`(max-width: ${bottomSheetBreakpoint}px)`).matches
            || (event.target as HTMLElement).closest('button, input, a, [role="button"]')) {
            return;
        }

        const target = closeAnimationSelector
            ? refs.floating.current?.querySelector<HTMLElement>(closeAnimationSelector)
            : refs.floating.current;
        if (!target) {
            return;
        }

        event.currentTarget.setPointerCapture(event.pointerId);
        target.style.animation = 'none';
        target.style.transition = 'none';
        sheetDragRef.current = { pointerId: event.pointerId, startY: event.clientY, offsetY: 0, target };
    }, [bottomSheetBreakpoint, bottomSheetDismissible, closeAnimationSelector, refs.floating]);

    const handleBottomSheetPointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const drag = sheetDragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) {
            return;
        }

        drag.offsetY = Math.max(0, event.clientY - drag.startY);
        drag.target.style.transform = `translateY(${drag.offsetY}px)`;
    }, []);

    const handleBottomSheetPointerEnd = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const drag = sheetDragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) {
            return;
        }

        sheetDragRef.current = undefined;
        event.currentTarget.releasePointerCapture(event.pointerId);

        if (drag.offsetY > drag.target.getBoundingClientRect().height * .22) {
            const dismissDuration = 160;
            drag.target.style.transition = `transform ${dismissDuration}ms cubic-bezier(.4, 0, 1, 1)`;
            drag.target.style.transform = 'translateY(100%)';
            sheetDragTimeoutRef.current = window.setTimeout(() => {
                shouldReturnFocusOnCloseRef.current = returnFocusOnClose;
                setIsClosing(false);
                setOpen(false);
            }, dismissDuration);
            return;
        }

        drag.target.style.transition = 'transform 160ms cubic-bezier(.2, .8, .2, 1)';
        drag.target.style.transform = 'translateY(0)';
        sheetDragTimeoutRef.current = window.setTimeout(() => {
            drag.target.style.removeProperty('transition');
        }, 160);
    }, [returnFocusOnClose]);

    // Build trigger and merge reference props ON THE SAME ELEMENT as the ref
    const rawTrigger = trigger({
        ref: (refs.setReference as unknown) as React.Ref<HTMLElement>,
        onClick: () => handleOpenChange(!open),
        'aria-expanded': open
    });

    const triggerNode = React.isValidElement(rawTrigger)
        ? React.cloneElement(
              rawTrigger as any,
              getReferenceProps({
                  // onClick: () => setOpen(o => !o),
                  'aria-expanded': open
              })
          )
        : rawTrigger;

    const floatingContent = (
        <div
            className={floatClassName}
            ref={setFloatingRef}
            {...getFloatingProps({
                id: floatingId,
                'aria-labelledby': labelledBy,
                'aria-modal': popupRole === 'dialog' ? false : undefined
            })}
            data-placement={resolvedPlacement}
            data-closing={isClosing || undefined}
            style={{
                ...floatingStyles,
                '--local-dropdown-open-animation-duration': `${openAnimationMs}ms`,
                '--local-dropdown-close-animation-duration': `${closeAnimationMs}ms`,
                pointerEvents: isClosing ? 'none' : undefined,
                zIndex: 1000
            } as React.CSSProperties}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerDownCapture={handleBottomSheetPointerDown}
            onPointerMoveCapture={handleBottomSheetPointerMove}
            onPointerUpCapture={handleBottomSheetPointerEnd}
            onPointerCancelCapture={handleBottomSheetPointerEnd}
        >
            {children(() => {
                // Focus on the trigger before closing to prevent there being no active element after the dropdown closes
                // @ts-ignore - Argument of type '{ focusVisible: boolean; }' is not assignable to parameter of type 'FocusOptions'.
                (refs.domReference.current as HTMLElement | null)?.focus({ focusVisible: false, preventScroll: true });
                handleOpenChange(false);
            })}
        </div>
    );

    return (
        <>
            {triggerNode}

            {(open || isClosing) && MsDyn365.isBrowser && (
                <FloatingPortal>
                    {backdropClassName && (
                        <div
                            aria-hidden={true}
                            className={backdropClassName}
                            data-closing={isClosing || undefined}
                            style={{
                                '--local-dropdown-open-animation-duration': `${openAnimationMs}ms`,
                                '--local-dropdown-close-animation-duration': `${closeAnimationMs}ms`
                            } as React.CSSProperties}
                            onPointerDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (!isClosing) {
                                    handleOpenChange(false);
                                }
                            }}
                        />
                    )}
                    <FloatingFocusManager context={context} disabled={isClosing} modal={false} initialFocus={-1}>
                        {floatingContent}
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </>
    );
}
